import { useTable, type RowData, type ColumnFiltersState, type Column } from '@tanstack/react-table';
import { useState, useEffect } from 'react';
import { dataTableFeatures, type DataTableColumn } from '@/shared/ui/dataTableFeatures';

type DataTableProps<T extends RowData> = {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowId?: (row: T) => string;
  className?: string;
  initialColumnFilters?: ColumnFiltersState;
  enablePagination?: boolean;
  pageSize?: number;
};

export function FilterInput<T extends RowData, V>({
  column,
}: {
  column: Column<typeof dataTableFeatures, T, V>;
}) {
  const meta = column.columnDef.meta;
  const filterType = meta?.filterType ?? 'text';
  const filterValue = column.getFilterValue() ?? '';

  if (filterType === 'none') {
    return null;
  }

  if (filterType === 'select') {
    return (
      <select
        value={filterValue as string}
        onChange={(e) => column.setFilterValue(e.target.value || undefined)}
        className="data-table__filter-select"
        aria-label={`Filter ${column.columnDef.header}`}
      >
        <option value="">All</option>
        {meta?.filterOptions?.map((opt: { label: string; value: string }) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      /* Not cosmetic, and not reachable from CSS. An <input> without `size`
         defaults to 20 characters, and that intrinsic width becomes the column's
         floor in an auto-layout table — `width: 100%` in the stylesheet cannot
         undo it, because the column is sized from the content's intrinsic
         contribution before any percentage resolves. (`min-width: 0` does not
         help either; measured, it changes nothing.)

         The visible cost was every column being about the same width regardless
         of what was in it: on Admin → Featured, "Pos" — a single digit — took
         151px while the titles wrapped inside 243px. With size=1 the floor
         disappears, `width: 100%` still fills whatever the column ends up being,
         and the widths follow the data: Pos 71px, Title 367px. */
      size={1}
      value={(filterValue as string) ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
      placeholder={meta?.placeholder ?? 'Filter...'}
      className="data-table__filter-input"
      aria-label={`Filter ${column.columnDef.header}`}
    />
  );
}

export function DataTable<T extends RowData>({
  columns,
  data,
  getRowId,
  className,
  initialColumnFilters,
  enablePagination = false,
  pageSize = 20,
}: DataTableProps<T>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters ?? []);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    if (initialColumnFilters) {
      setColumnFilters(initialColumnFilters);
    }
  }, [initialColumnFilters]);

  /* Pull `pageIndex` back when the data shrinks under it.
     `pagination` is controlled state held here, so nothing else clamps it: a row
     removed from the last page leaves the index pointing past the end, and the
     table renders an empty body under a footer reading "Showing 41–40 of 40".
     Newly reachable because Admin → Featured is the first of these tables with a
     row-level delete; filtering hits the same path. */
  const rowCount = data.length;
  useEffect(() => {
    if (!enablePagination) return;
    setPagination((current) => {
      const lastPage = Math.max(0, Math.ceil(rowCount / current.pageSize) - 1);
      return current.pageIndex > lastPage ? { ...current, pageIndex: lastPage } : current;
    });
  }, [rowCount, enablePagination]);

  const table = useTable<typeof dataTableFeatures, T>({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    state: {
      columnFilters,
      pagination: enablePagination ? pagination : { pageIndex: 0, pageSize: 99999999 },
    },
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: enablePagination ? setPagination : undefined,
  });

  return (
    <>
      <div className={className ? `wallet-activity__table-wrap ${className}` : 'wallet-activity__table-wrap'}>
        <table className="wallet-activity__table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    style={header.column.columnDef.meta?.align === 'left' ? { textAlign: 'left' } : undefined}
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="data-table__sort-button"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <table.FlexRender header={header} />
                        <span className={`data-table__sort-icon ${header.column.getIsSorted() ? 'data-table__sort-icon--active' : ''}`} aria-hidden="true">
                          {header.column.getIsSorted() === 'asc'
                            ? '▲'
                            : header.column.getIsSorted() === 'desc'
                              ? '▼'
                              : '⇅'}
                        </span>
                      </button>
                    ) : (
                      <table.FlexRender header={header} />
                    )}
                  </th>
                ))}
              </tr>
            ))}
            {table.getHeaderGroups().map((group) => (
              <tr key={`filter-${group.id}`} className="data-table__filter-row">
                {group.headers.map((header) => {
                  const canFilter = header.column.getCanFilter();
                  const meta = header.column.columnDef.meta;
                  const showFilter = canFilter && meta?.filterType !== 'none';
                  return (
                    <th
                      key={`filter-${header.id}`}
                      scope="col"
                      style={header.column.columnDef.meta?.align === 'left' ? { textAlign: 'left' } : undefined}
                    >
                      {showFilter ? <FilterInput column={header.column} /> : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getAllCells().map((cell) => (
                  <td key={cell.id}>
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {enablePagination && (
        <div className="data-table__pagination">
          <div className="data-table__pagination-info">
            Showing {table.getFilteredRowModel().rows.length === 0 ? 0 : table.state.pagination.pageIndex * table.state.pagination.pageSize + 1}–
            {Math.min(
              (table.state.pagination.pageIndex + 1) * table.state.pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} entries
          </div>
          <div className="data-table__pagination-controls">
            <button
              type="button"
              className="button secondary compact"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              First
            </button>
            <button
              type="button"
              className="button secondary compact"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </button>
            <span className="data-table__pagination-page">
              Page <strong>{table.state.pagination.pageIndex + 1}</strong> of{' '}
              <strong>{table.getPageCount()}</strong>
            </span>
            <button
              type="button"
              className="button secondary compact"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </button>
            <button
              type="button"
              className="button secondary compact"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </>
  );
}
